import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api.js';

export type User = { id: string; email: string; role: 'owner' | 'admin' };
export type Venue = { id: string; name: string; slug: string; maxScreens: number };

type SessionValue = {
  user: User | null;
  venues: Venue[];
  venue: Venue | null;
  loading: boolean;
  selectVenue: (id: string) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);

const SELECTED_KEY = 'lobbyfun.selectedVenue';

/** Who is signed in and which venue they are looking at (PART F6). */
export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<{ user: User; venues: Venue[] }>('/api/auth/me');
      setUser(me.user);
      setVenues(me.venues);

      setSelectedId((current) => {
        const remembered = current ?? safeGet(SELECTED_KEY);
        const stillExists = me.venues.some((v) => v.id === remembered);
        return stillExists ? remembered : (me.venues[0]?.id ?? null);
      });
    } catch {
      // 401 is the normal signed-out case, not an error worth surfacing.
      setUser(null);
      setVenues([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectVenue = useCallback((id: string) => {
    setSelectedId(id);
    safeSet(SELECTED_KEY, id);
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    setUser(null);
    setVenues([]);
    setSelectedId(null);
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      user,
      venues,
      venue: venues.find((v) => v.id === selectedId) ?? venues[0] ?? null,
      loading,
      selectVenue,
      refresh,
      logout,
    }),
    [user, venues, selectedId, loading, selectVenue, refresh, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode — the selection just does not persist */
  }
}
